/* eslint-disable @typescript-eslint/no-explicit-any */
import { Ban, CheckCircle, Mail, Users, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { Organization, User, InvitationWithDetails, UserRoles } from '@/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import { useAuthStore } from '@/stores/authStore';
import { updateUserOrganization, updateUserRole, toggleUserActive } from '@/services/userService';
import { createInvitation, getOrganizationInvitations, revokeInvitation, cleanupInvitations } from '@/services/invitationService';
import organizationService from '@/services/organizationService';
import { notify } from '@/lib/notify';
import { useTranslation } from 'react-i18next';

interface OrganizationMembersProps {
  organization: Organization;
  canEdit: boolean;
}

export const OrganizationMembers: React.FC<OrganizationMembersProps> = ({
  organization,
  canEdit
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'members' | 'invitations'>('members');
  const [invitations, setInvitations] = useState<InvitationWithDetails[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [organization]);

  useEffect(() => {
    if (activeTab === 'invitations') {
      loadInvitations();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    if (!organization) return;

    try {
      setLoadingUsers(true);
      const data = await organizationService.getOrganizationUsers(organization.id);
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadInvitations = async () => {
    if (!organization) return;

    try {
      setLoadingInvitations(true);
      const data = await getOrganizationInvitations(organization.id);
      setInvitations(data);
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleUnassign = async (userId: string) => {
    try {
      setSaving(true);
      await updateUserOrganization(userId, null);
      await loadUsers();
    } catch (err) {
      console.error('Error unassigning user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAdmin = async (userId: string, currentRole: string) => {
    const newRole = currentRole === UserRoles.Admin ? UserRoles.User : UserRoles.Admin;
    const confirmMsg = newRole === UserRoles.Admin ? t('organizationMembers.promoteConfirm') : t('organizationMembers.demoteConfirm');

    if (!confirm(confirmMsg)) {
      return;
    }

    try {
      setSaving(true);
      await updateUserRole(userId, newRole);
      notify.show({
        variant: 'success',
        title: newRole === UserRoles.Admin ? t('organizationMembers.adminGranted') : t('organizationMembers.adminRevoked'),
        description: newRole === UserRoles.Admin ? t('organizationMembers.adminGrantedDesc') : t('organizationMembers.adminRevokedDesc'),
      });
      await loadUsers();
    } catch (err: any) {
      console.error('Error updating user role:', err);
      notify.error(err.response?.data?.detail || t('organizationMembers.roleUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    const confirmMsg = isActive ? t('organizationMembers.reactivateConfirm') : t('organizationMembers.deactivateConfirm');
    if (!confirm(confirmMsg)) {
      return;
    }
    try {
      setSaving(true);
      await toggleUserActive(userId, isActive);
      notify.show({
        variant: 'success',
        title: isActive ? t('organization.members.activated', 'User activated') : t('organization.members.deactivated', 'User deactivated'),
      });
      await loadUsers();
    } catch (err: any) {
      console.error('Error toggling user active status:', err);
      notify.error(err.response?.data?.detail || t('organization.members.toggleActiveError', 'Erreur lors de la mise à jour du statut'));
    } finally {
      setSaving(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization) return;

    setInviteError(null);

    if (!inviteEmail.trim()) {
      setInviteError(t('invitations.errors.emailRequired'));
      return;
    }

    if (!validateEmail(inviteEmail)) {
      setInviteError(t('invitations.errors.emailInvalid'));
      return;
    }

    setSaving(true);

    try {
      await createInvitation({
        email: inviteEmail.trim(),
        organization_id: organization.id
      });

      notify.show({
        variant: 'success',
        title: t('invitations.invitationSent'),
        description: t('organizationMembers.invitationSentTo', { email: inviteEmail.trim() }),
      });
      setInviteEmail('');
      setShowInviteForm(false);
      await loadInvitations();
    } catch (err: any) {
      console.error('Error creating invitation:', err);

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (detail.includes('already exists')) {
          setInviteError(t('invitations.errors.emailAlreadyExists'));
        } else if (detail.includes('pending invitation')) {
          setInviteError(t('invitations.errors.pendingInvitationExists'));
        } else {
          setInviteError(detail);
        }
      } else {
        setInviteError(t('invitations.errors.sendFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!confirm(t('organizationMembers.revokeConfirm'))) {
      return;
    }

    try {
      await revokeInvitation(invitationId);
      notify.success(t('invitations.invitationRevoked'));
      await loadInvitations();
    } catch (err) {
      console.error('Error revoking invitation:', err);
      notify.error(t('invitations.errors.revokeFailed'));
    }
  };

  const handleCleanupInvitations = async () => {
    if (!organization?.id) return;

    if (!confirm(t('invitations.confirmCleanup', 'Supprimer toutes les invitations acceptées, refusées et expirées ?'))) {
      return;
    }

    try {
      const result = await cleanupInvitations(organization.id);
      notify.success(t('organizationMembers.cleanupDeleted', { count: result.deleted_count }));
      await loadInvitations();
    } catch (err) {
      console.error('Error cleaning up invitations:', err);
      notify.error(t('invitations.errors.cleanupFailed', 'Erreur lors du nettoyage'));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏱️';
      case 'accepted':
        return '✅';
      case 'expired':
        return '⏰';
      case 'revoked':
        return '❌';
      default:
        return '•';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'expired':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'revoked':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (!canEdit) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <CardTitle>{t('organizationMembers.membersCount', { count: users.length })}</CardTitle>
          </div>
          {activeTab === 'members' && !showInviteForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInviteForm(true)}
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              {t('organizationMembers.inviteMember')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex border-b border-border mb-4">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'members'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="h-4 w-4 inline-block mr-2" />
            {t('organizationMembers.activeMembers', { count: users.length })}
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'invitations'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail className="h-4 w-4 inline-block mr-2" />
            {t('organizationMembers.invitationsCount', { count: invitations.length })}
          </button>
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            {/* Invitation Form */}
            {showInviteForm && (
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-base">{t('organizationMembers.inviteNewMember')}</CardTitle>
                  <CardDescription>
                    {t('organizationMembers.inviteDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSendInvitation} className="space-y-3">
                    <div>
                      <Label htmlFor="invite-email">{t('invitations.emailLabel')}</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        placeholder={t('invitations.emailPlaceholder')}
                        value={inviteEmail}
                        onChange={(e) => {
                          setInviteEmail(e.target.value);
                          setInviteError(null);
                        }}
                        disabled={saving}
                        className="mt-1"
                      />
                      {inviteError && (
                        <p className="mt-1 text-sm text-destructive">{inviteError}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={saving || !inviteEmail.trim()}>
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            {t('common.sending', 'Envoi...')}
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4 mr-2" />
                            {t('invitations.send')}
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowInviteForm(false);
                          setInviteEmail('');
                          setInviteError(null);
                        }}
                        disabled={saving}
                        aria-label={t('global.cancel')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Members List */}
            {loadingUsers ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-sm text-muted-foreground">{t('organizationMembers.loadingMembers')}</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                {t('organizationMembers.noMembersInOrg')}
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.id} className={`flex items-center justify-between p-3 border border-border rounded-lg ${u.is_active === false ? 'opacity-60' : ''}`}>
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        {u.name || u.email}
                        {u.is_active === false && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
                            {t('organization.members.suspended', 'Suspendu')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                      {u.role && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                          u.role === UserRoles.Admin
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {u.role === UserRoles.Admin ? `👤 ${t('global.admin', 'Admin')}` : u.role}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={u.role === UserRoles.Admin ? 'outline' : 'default'}
                        disabled={saving || u.id === user?.id}
                        onClick={() => handleToggleAdmin(u.id, u.role || UserRoles.User)}
                        className={u.role === UserRoles.Admin ? '' : 'bg-blue-600 hover:bg-blue-700'}
                      >
                        {u.role === UserRoles.Admin ? t('organization.members.demoteAdmin') : t('organization.members.promoteAdmin')}
                      </Button>
                      {u.role !== UserRoles.Admin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={saving || u.id === user?.id}
                          onClick={() => handleToggleActive(u.id, u.is_active === false)}
                          className={u.is_active === false ? 'text-green-600 hover:text-green-700' : 'text-orange-500 hover:text-orange-600'}
                          title={u.is_active === false
                            ? t('organization.members.activate', 'Activer')
                            : t('organization.members.deactivate', 'Désactiver')}
                          aria-label={u.is_active === false
                            ? t('organization.members.activate', 'Activer')
                            : t('organization.members.deactivate', 'Désactiver')}
                        >
                          {u.is_active === false
                            ? <CheckCircle className="h-4 w-4" />
                            : <Ban className="h-4 w-4" />}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saving || u.id === user?.id}
                        onClick={() => handleUnassign(u.id)}
                      >
                        {t('organizationMembers.removeMember')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="space-y-4">
            {/* Cleanup button */}
            {invitations.length > 0 && invitations.some(inv => inv.status !== 'pending') && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCleanupInvitations}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('invitations.cleanup', 'Nettoyer les invitations')}
                </Button>
              </div>
            )}

            {loadingInvitations ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-sm text-muted-foreground">{t('invitations.loadingInvitations')}</p>
              </div>
            ) : invitations.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">{t('invitations.noInvitations')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-medium">{invitation.email}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {t('organizationMembers.invitedBy', { name: invitation.inviter_name || t('organizationMembers.unknownInviter'), date: new Date(invitation.created_at).toLocaleDateString() })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('organizationMembers.expiresOn', { date: new Date(invitation.expires_at).toLocaleDateString() })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(invitation.status)}`}>
                        {getStatusIcon(invitation.status)} {t(`invitations.${invitation.status}`)}
                      </span>
                      {invitation.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevokeInvitation(invitation.id)}
                          className="text-destructive"
                        >
                          {t('invitations.revoke')}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
